import { KEY_VALUES } from './constants'
import {
  EDIT_MODE_ASCII,
  EDIT_MODE_HEX,
  SELECTION_DIRECTION_BACKWARD,
  SELECTION_DIRECTION_FORWARD,
} from './constants/editor-states'
import {
  EditModeType,
  HexViewerAction,
  SelectionDirectionType,
  SelectionType,
  SetSelectionRangeCallback,
} from './types'
import Keycoder from 'keycoder'

interface HexViewerHelperParams {
  event: React.KeyboardEvent<HTMLInputElement>
  readOnly: boolean
  nybbleOffset: number
  nybbleHigh: number | null
  selection: SelectionType
  setValue: (offset: number, value: number) => void
  setState: (value: HexViewerAction) => void
  setSelectionRange: SetSelectionRangeCallback
}

const defaultSelectionRange = {
  start: null as number | null,
  end: null as number | null,
  direction: null as SelectionDirectionType | null,
}

export function goToFirstCharacter(
  event: React.KeyboardEvent<HTMLInputElement>,
  selection: SelectionType,
  setSelectionRange: SetSelectionRangeCallback
) {
  const { shiftKey } = event

  const range = { ...defaultSelectionRange }

  if (shiftKey) {
    const end = selection.direction === SELECTION_DIRECTION_BACKWARD ? selection.end : selection.start
    range.start = 0
    range.end = end
    range.direction = SELECTION_DIRECTION_BACKWARD
  } else {
    range.start = 0
  }
  setSelectionRange(range.start, range.end, range.direction)
  event.preventDefault()
}

export function goToLastCharacter(
  event: React.KeyboardEvent<HTMLInputElement>,
  selection: SelectionType,
  dataLength: number,
  setSelectionRange: SetSelectionRangeCallback
) {
  const { shiftKey } = event

  const range = { ...defaultSelectionRange }

  if (shiftKey) {
    const start = selection.direction === SELECTION_DIRECTION_BACKWARD ? selection.end : selection.start
    range.start = start
    range.end = dataLength
    range.direction = SELECTION_DIRECTION_FORWARD
  } else {
    range.start = dataLength - 1
  }
  setSelectionRange(range.start, range.end, range.direction)
  event.preventDefault()
}

export function toggleHexAscii(
  event: React.KeyboardEvent<HTMLInputElement>,
  editMode: EditModeType,
  showAscii: boolean,
  setState: (value: HexViewerAction) => void
) {
  const { shiftKey } = event

  if (!shiftKey && showAscii && editMode === EDIT_MODE_HEX) {
    setState({ editMode: EDIT_MODE_ASCII })
    event.preventDefault()
  } else if (shiftKey && editMode === EDIT_MODE_ASCII) {
    setState({ editMode: EDIT_MODE_HEX })
    event.preventDefault()
  }
}

export function goToStartOfLine(
  event: React.KeyboardEvent<HTMLInputElement>,
  selection: SelectionType,
  columns: number,
  setSelectionRange: SetSelectionRangeCallback
) {
  const { shiftKey } = event

  const selectionAnchor = selection.direction === SELECTION_DIRECTION_BACKWARD ? selection.start : selection.end
  const range = { ...defaultSelectionRange }

  if (shiftKey) {
    const cursorPosition = columns * Math.floor(Math.max(0, selectionAnchor - 1) / columns)
    if (selection.direction === SELECTION_DIRECTION_BACKWARD) {
      range.start = selection.end
      range.end = cursorPosition
    } else {
      range.start = selection.start
      range.end = cursorPosition
    }
  } else {
    const cursorPosition = columns * Math.floor(selectionAnchor / columns)
    range.start = cursorPosition
  }
  setSelectionRange(range.start, range.end)
  event.preventDefault()
}

export function goToEndOfLine(
  event: React.KeyboardEvent<HTMLInputElement>,
  selection: SelectionType,
  columns: number,
  dataLength: number,
  setSelectionRange: SetSelectionRangeCallback
) {
  const { shiftKey } = event

  const selectionAnchor = selection.direction === SELECTION_DIRECTION_BACKWARD ? selection.start : selection.end
  const range = { ...defaultSelectionRange }

  if (shiftKey) {
    const cursorPosition = columns * (Math.floor(selectionAnchor / columns) + 1)
    if (selection.direction === SELECTION_DIRECTION_BACKWARD) {
      range.start = selection.end
      range.end = cursorPosition
    } else {
      range.start = selection.start
      range.end = cursorPosition
    }
  } else {
    const cursorPosition = columns * (Math.floor(selectionAnchor / columns) + 1) - 1
    range.start = Math.min(cursorPosition, dataLength - 1)
  }
  setSelectionRange(range.start, range.end)
  event.preventDefault()
}

export function goBackOneRowOrColumn(
  event: React.KeyboardEvent<HTMLInputElement>,
  selection: SelectionType,
  offsetAmounts: { [key: number]: number },
  isSelection: boolean,
  setSelectionRange: SetSelectionRangeCallback
) {
  const { shiftKey, which } = event

  const offset = offsetAmounts[which] || 1
  const range = { ...defaultSelectionRange }

  if (shiftKey) {
    if (selection.direction === SELECTION_DIRECTION_BACKWARD) {
      range.start = selection.end
      range.end = selection.start - offset
    } else {
      range.start = selection.start
      range.end = selection.end - offset
    }
  } else {
    const cursorPosition = isSelection ? selection.start : selection.start - offset
    range.start = Math.max(0, cursorPosition)
  }
  setSelectionRange(range.start, range.end)
  event.preventDefault()
}

export function goForwardOneRowOrColumn(
  event: React.KeyboardEvent<HTMLInputElement>,
  selection: SelectionType,
  offsetAmounts: { [key: number]: number },
  isSelection: boolean,
  setSelectionRange: SetSelectionRangeCallback,
  dataLength: number
) {
  const { shiftKey, which } = event

  const offset = offsetAmounts[which] || 1
  const range = { ...defaultSelectionRange }

  if (shiftKey) {
    if (selection.direction === SELECTION_DIRECTION_BACKWARD) {
      range.start = selection.end
      range.end = selection.start + offset
    } else {
      range.start = selection.start
      range.end = selection.end + offset
    }
  } else {
    const cursorPosition = isSelection ? selection.end - 1 : selection.end + offset
    range.start = Math.min(cursorPosition, dataLength - 1)
  }
  setSelectionRange(range.start, range.end)
  event.preventDefault()
}

export function clearPreviousCharacter(params: HexViewerHelperParams) {
  const { event, readOnly, nybbleOffset, nybbleHigh, selection, setValue, setState, setSelectionRange } = params
  const { shiftKey } = event

  if (!readOnly) {
    if (nybbleOffset && nybbleHigh != null) {
      setState({
        nybbleHigh: null,
        nybbleOffset: 0,
      })
    } else if (shiftKey) {
      setValue(selection.end, 0x00)
      setSelectionRange(selection.end)
    } else {
      setValue(selection.end - 1, 0x00)
      setSelectionRange(selection.end - 1)
    }
  }
  event.preventDefault()
}

export function clearNextCharacter(params: HexViewerHelperParams) {
  const { event, readOnly, nybbleOffset, nybbleHigh, selection, setValue, setState, setSelectionRange } = params
  const { shiftKey } = event

  if (!readOnly) {
    if (nybbleOffset && nybbleHigh != null) {
      setState({
        nybbleHigh: null,
        nybbleOffset: 0,
      })
    } else if (shiftKey) {
      setValue(selection.end, 0x00)
      setSelectionRange(selection.end)
    } else {
      setValue(selection.end, 0x00)
      setSelectionRange(selection.end + 1)
    }
  }
  event.preventDefault()
}

export function editHexValue(params: HexViewerHelperParams, dataLength: number, isSelection: boolean) {
  const { event, readOnly, nybbleOffset, nybbleHigh, selection, setValue, setState, setSelectionRange } = params
  const { which } = event

  if (!readOnly) {
    const nybbleValue = KEY_VALUES[which]
    if (nybbleOffset && nybbleHigh != null) {
      const value = (nybbleHigh << 4) | nybbleValue
      setValue(selection.end, value)
      setSelectionRange(Math.min(selection.end + 1, dataLength - 1))
    } else {
      if (isSelection) {
        setSelectionRange(selection.direction === SELECTION_DIRECTION_BACKWARD ? selection.start : selection.end - 1)
      }
      setState({
        nybbleOffset: 1,
        nybbleHigh: nybbleValue,
      })
    }
  }
  event.preventDefault()
}

export function editAsciiValue(
  event: React.KeyboardEvent<HTMLInputElement>,
  readOnly: boolean,
  selection: SelectionType,
  setValue: (offset: number, value: number) => void,
  setSelectionRange: SetSelectionRangeCallback,
  dataLength: number
) {
  const { shiftKey } = event

  if (!readOnly) {
    const key = Keycoder.fromEvent(event.nativeEvent)
    if (key.charCode != null) {
      const value = shiftKey ? key.shift.charCode : key.charCode
      if (value != null) {
        setValue(selection.end, value)
        setSelectionRange(Math.min(selection.end + 1, dataLength - 1))
      }
    }
  }
  event.preventDefault()
}
